import { InputAdornment, TextField, Box } from "@mui/material";
import { useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import classes from "../CustomInputs.module.css";

// Tooltip
import CustomTooltip from "../../Tooltip/CustomTooltip/CustomTooltip";
import TextEditorInput from "./TextEditorInput";
import ContentEditable from "react-contenteditable";
import { getDecimalNumberStringByLimit, getDecodedHtmlString, removeHtmlTags } from "../../../../utils/CommonUtils";
import { LENGTHS } from "../../../../config/Config";
import { InputInfo } from "../../../../entities/UIEntities/Form Entities/InputInfo";


const TextInput = forwardRef((props, ref) => {
  const textFieldInputRef = useRef();
  const showError = props.isStepSubmitted && props.hasError
  const relevantValueLength = props?.excludeHtmlTags ? getDecodedHtmlString(removeHtmlTags(props?.value), true)?.length : props?.value?.length
  
  useImperativeHandle(ref , () => ({
    textFieldInputRef
  }));

  useEffect(() => {
    if (props.handleError) {
      props.handleError({ [props.id]: props.hasError });
    }
    if (props.handleInputInfo) {
      props.handleInputInfo(new InputInfo(props.id, props.value, props.isRequired, props.hasError));
    }
  }, [props.value]);

  const handleChange = (event) => {
    let { name, value } = event.target ? event.target : event;
    if (props.type === "number") {
      value = parseFloat(getDecimalNumberStringByLimit(value, LENGTHS.TO_FIXED_LENGTH));
      if (isNaN(value)) value = "";
      if (value < props.minValue) value = props.minValue;
      if (value > props.maxValue) value = props.maxValue;
    }

    // If char counter is active, and reached limit disallows user to enter new value
    // onPaste prop - currently limited only if counters props passed
    if (props?.showCharCounter && props?.charCounterLimit) {
      // Check if need to remove tags for the char counter limit check
      const valueCopy = props?.excludeHtmlTags ? getDecodedHtmlString(removeHtmlTags(value), true) : value;

      // Check if user pasted the value
      const isPasted = event?.nativeEvent?.inputType?.startsWith("insertFromPaste");
      
      if (valueCopy?.length > props?.charCounterLimit) {
        if (props?.onPaste && isPasted) {
          // If exceeds limit & user pasted & onPaste callback passed, use it
          props.onPaste();
        }

        // Set the old known value (disallows user to enter new value)
        props.onChange({ [name]: props?.value });
      }
      else {
        // Set the new received value (sets value normally)
        props.onChange({ [name]: value });
      }
    }
    else {
      // Set the new received value (sets value normally)
      props.onChange({ [name]: value });
    }
  };

  let multiLine = props.classesToAdd ? true : false
  let customClasses = [classes.customTextField, props.classesToAdd && props.maxRows && props.isEditable ? classes.customMessageBodyEditableTextField : props.classesToAdd && props.maxRows? classes.customMessageBodyTextField : props.classesToAdd ? classes.customExtendedTextField : ''].join(' ')

  return (
    <>
      {props?.isEditable && <TextEditorInput value={props.value} inputRef={textFieldInputRef} handleChange={handleChange} />}
      <div className={customClasses}>
        {props?.isEditable ? 
          <>
            <div className={showError ? [classes.editableLabel, classes.editableError, classes.noBorder].join(' ') : classes.editableLabel}>{props.label}</div>
            <div className={classes.customTextField}>
              <ContentEditable
                className={showError ? [classes.editableError, classes.editable].join(' ') : classes.editable}  // classes.customClasses
                id={props.id}
                name={props.id}
                html={props.value ?? ""}
                ref={textFieldInputRef}
                onChange={(event) => {
                  event.target.name = props.id;
                  handleChange(event);
                }}  
              />
              {props.showTooltip && (
                <CustomTooltip {...props} />
              )}
              {showError && (
                <div className={classes.editableErrorContainer}>
                  <span className={[classes.noBorder, classes.editableError].join(' ')}>{props.errorMessage}</span>
                </div>
              )}
            </div> 
          </>
          : 
          <TextField
            autoComplete="true"
            autoFocus={props.autoFocus}
            type={props.type}
            disabled={props.disabled}
            fullWidth
            label={props.label}
            inputRef={textFieldInputRef}
            id={props.id}
            name={props.id}
            multiline={multiLine}
            maxRows={props.classesToAdd && props.maxRows ? props.maxRows : 3} // 3
            className={classes.textField}
            value={props.value ?? ""}
            onChange={handleChange}
            onKeyPress={(event) => {
              if (event.key === "Enter" && !multiLine || 
                ["E", "e", "+", "-"].includes(event.key) && props.type === "number") {
                event.preventDefault();
              }
            }}
            error={showError}
            helperText={
              showError ? props.errorMessage : ""
            }
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {props.showTooltip && (
                    <CustomTooltip {...props} />
                  )}
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment
                  className={classes.inputAdornedStart}
                  position="start"
                >
                  {props.startAdornment && <span>{props.startAdornment}</span>}
                </InputAdornment>
              ),
              classes: {
                notchedOutline: props.classesToAdd && props.maxRows ? classes.extendedMessageFiled : props.classesToAdd ? classes.extendedFiled : classes.filed,
              },
              min: props.minValue,
              max: props.maxValue
            }}
            FormHelperTextProps={{
              classes: {
                error: props.classesToAdd && props.maxRows ? [classes.error, classes.extendedFieldError].join(' ') : classes.error,
              },
            }}
          />
        }
      </div>
      <Box className={classes.customTextField}>
        {((props?.showCharCounter && props?.charCounterLimit) && relevantValueLength <= props?.charCounterLimit) &&
          <Box className={classes.charsCounter} sx={props.charCounterDisplaySx ? props.charCounterDisplaySx : null}>{`${relevantValueLength} / ${props?.charCounterLimit}`}</Box>
        }
      </Box>
    </>
  );
}); // added ) - for forward ref

export default TextInput;
